import React, { useCallback, useEffect, useMemo, useState } from "react";
import SEO from "../components/SEO";
import GroupOne from "../components/GroupOne";
import GroupTwo from "../components/GroupTwo";
import GroupThree from "../components/GroupThree";
import GroupFour from "../components/GroupFour";
import GroupFive from "../components/GroupFive";
import GroupSix from "../components/GroupSix";
import GroupSeven from "../components/GroupSeven";
import GroupNine from "../components/GroupNine";
import { CalculatorPersistenceProvider, useCalculatorPersistence, useCalculatorStoredValue } from "../contexts/CalculatorPersistenceContext";
import { useGrades } from "../contexts/GradesContext";
import { useTZ } from "../contexts/TimezoneContext";
import { BOUNDARY_OPTIONS, getTimezoneOptionsForBoundary } from "../utils/boundaryCatalog";
import { DEFAULT_CALCULATOR_BOUNDARY, DEFAULT_CORE_DATA } from "../utils/calculatorStorage";
import { FlowActionsProvider, useFlowActions } from "../features/calculator/contexts/FlowActionsContext";
import FlowHeadline from "../features/calculator/components/FlowHeadline";
import FlowOverview from "../features/calculator/components/FlowOverview";
import FlowLiveSummary from "../features/calculator/components/FlowLiveSummary";
import SelectField from "../shared/components/SelectField";
import "../features/calculator/styles/calculatorHtml.css";

const GROUP_IDS = ["1", "2", "3", "4", "5", "6"];
const SUBJECTS = [GroupOne, GroupTwo, GroupThree, GroupFour, GroupFive, GroupSix];

function CalculatorContent() {
  const { grades, coreData } = useGrades();
  const { tz, setTZ } = useTZ();
  const { getSnapshot, setCalculatorValue } = useCalculatorPersistence();
  const actions = useFlowActions();
  const [announcement, setAnnouncement] = useState("");
  const [boundary, setBoundary] = useCalculatorStoredValue("boundary", DEFAULT_CALCULATOR_BOUNDARY);
  const timezoneOptions = useMemo(() => getTimezoneOptionsForBoundary(boundary), [boundary]);

  useEffect(() => {
    document.documentElement.classList.add("calculator-html-root");
    return () => document.documentElement.classList.remove("calculator-html-root");
  }, []);
  useEffect(() => { setCalculatorValue("grades", grades, {}); }, [grades, setCalculatorValue]);
  useEffect(() => { setCalculatorValue("coreData", coreData, DEFAULT_CORE_DATA); }, [coreData, setCalculatorValue]);
  const selectTimezone = useCallback(value => {
    setTZ(value);
    setCalculatorValue("timezone", value, "tz1");
  }, [setCalculatorValue, setTZ]);
  useEffect(() => {
    if (!timezoneOptions.includes(tz)) selectTimezone(timezoneOptions[0]);
  }, [selectTimezone, timezoneOptions, tz]);

  const activeSubjects = useMemo(() => GROUP_IDS.map(id => grades[id]).filter(subject => subject && subject.grade != null), [grades]);
  const totalSubjectPoints = activeSubjects.reduce((sum, subject) => sum + subject.grade, 0);
  const corePoints = coreData?.corePoints ?? 0;
  const fullTotalPoints = totalSubjectPoints + corePoints;
  const handleExport = useCallback(async () => {
    const state = getSnapshot();
    const { generateResultsPDF } = await import("../utils/generateResultsPDF");
    await generateResultsPDF({ grades, coreData, boundary, tz, state });
  }, [grades, coreData, boundary, tz, getSnapshot]);
  const applyExample = () => { actions.apply("example"); setAnnouncement("Example marks entered for all subjects and the core."); };
  const clearMarks = () => { actions.apply("clear"); setAnnouncement("Marks cleared. Your subject choices are kept."); };

  return (
    <div className="calculator-html">
      <SEO
        title="IB Grade Calculator and Predictor | ibcalculator"
        description="Estimate your IB subject grades, total points, and diploma outcome using the latest official IB grade boundaries."
        path="/"
      />
      <div className="sheet">
        <section className="intro" aria-labelledby="page-title">
          <div className="hero-copy">
            <FlowHeadline />
            <p>Enter your marks to estimate your IB grades, total points, and diploma outcome using the latest official grade boundaries.</p>
            <div className="intro-actions">
              <button className="ib-action ib-action-primary" disabled={!actions.ready} onClick={applyExample}>Try example marks <span aria-hidden="true">+</span></button>
              <button className="ib-action" disabled={!actions.ready} onClick={clearMarks}>Clear marks</button>
            </div>
          </div>
          <FlowOverview grades={grades} coreData={coreData} total={fullTotalPoints} completed={activeSubjects.length} />
        </section>
        <section className="exam-settings" aria-label="Exam settings">
          <label className="setting">Exam session
            <SelectField label="Exam session" value={boundary} onChange={setBoundary} options={BOUNDARY_OPTIONS.map(option => ({ ...option, label: option.label.replace(/ \([^)]+\)$/, "") }))} />
          </label>
          <label className="setting">Exam timezone
            <SelectField label="Exam timezone" value={tz} onChange={selectTimezone} options={timezoneOptions.map(value => ({ value, label: value.toUpperCase().replace("TZ", "TZ ") }))} />
          </label>
          <p><span>Applies to all six subjects.</span><a href="/resources/timezone-finder">Find your timezone <span aria-hidden="true">&#8594;</span></a></p>
        </section>
        <div className="sheet-description"><h2>Your six subjects</h2><p>Use the mark on your paper, such as 28 out of 40. We'll apply the weights shown below each field.</p></div>
        <div id="subjects" aria-live="off">{SUBJECTS.map((Component, index) => <Component key={GROUP_IDS[index]} boundary={boundary} />)}</div>
        <GroupSeven />
        <GroupNine onExport={handleExport} />
      </div>
      <div className="summary-dock"><FlowLiveSummary grades={grades} coreData={coreData} total={fullTotalPoints} completed={activeSubjects.length} /></div>
      <p className="sr-only" role="status">{announcement}</p>
    </div>
  );
}

export default function Calculator() {
  return <CalculatorPersistenceProvider><FlowActionsProvider><CalculatorContent /></FlowActionsProvider></CalculatorPersistenceProvider>;
}
